import * as _ from "lodash";
import { ProgramContentType } from "data/BlockComponent";
import {
  CellType,
  MultipleSelectionType,
  PushNotificationType,
  SingleSelectionType,
} from "data/CellComponent";
import { getPercentageSum, getTaskIdFromTaskKey } from "logic/logics";
import { UserSimField } from "api/userSimApi";

// 교육용 모의 내담자 계정의 "자동 채우기" 대상 활동지
export const AUTO_FILL_TASK_IDS = [
  "4-1-A", // 생각함정
  "4-2-A", // 다양한 관점에서 생각해보기
  "4-5-1-A", // 책임 과다 다루기
  "4-5-2-A", // 근거 부족 다루기
  "4-5-3-A", // 부정 편향 다루기
  "4-5-4-A", // 평가 다루기
  "4-5-5-A", // 개념화 다루기
  "4-5-6-A", // 반추 다루기
  "8-10-A", // 레몬 맛 팝콘
  "12-2-A", // 경험 분리 훈련 - 생각
  "13-1-A", // 경험 분리 훈련 - 행동
  "9-2-A", // 나의 지난 노력들
  "2-2-T", // 생각기록지
];

// 마치기 알림(POST /user_sim/complete) 대상 — 자동 채우기 대상 + 직접 쓰는 활동지 (docs/user-sim-agent.md 5장)
export const USER_SIM_COMPLETE_TASK_IDS = [
  ...AUTO_FILL_TASK_IDS,
  "10-3-A", // 목표 설정하기 — 운영자가 직접 써서 모의 내담자의 출발점이 된다
];

// 자동적 사고 번호·환자 id 를 뗀 활동지 ID
const getBaseTaskId = (taskKey: string) =>
  getTaskIdFromTaskKey(taskKey).split("_")[0].split("#")[0];

export function isAutoFillTask(taskKey: string) {
  return AUTO_FILL_TASK_IDS.includes(getBaseTaskId(taskKey));
}

export function isUserSimCompleteTask(taskKey: string) {
  return USER_SIM_COMPLETE_TASK_IDS.includes(getBaseTaskId(taskKey));
}

// 칸 위치 키 — invalidTextareaIndexAtom 의 indexKey 와 같은 형식
export function getCellKey(
  blockIndex: number,
  lineIndex: number,
  cellIndex: number
) {
  return `${blockIndex}-${lineIndex}-${cellIndex}`;
}

export type AutoFillValues = Record<string, string | number | number[]>;

// 선택지를 고르면 생기는 칸(선택지의 additionalContent)의 키.
// AG-UI STATE_DELTA 의 JSON Pointer 경로(/values/<key>)에 그대로 쓰이므로 "/"·"~" 를 넣지 않는다
export function getOptionCellKey(
  fieldKey: string,
  optionIndex: number,
  lineIndex: number,
  cellIndex: number
) {
  return `${fieldKey}.${optionIndex}.${lineIndex}-${cellIndex}`;
}

// 다음에 열리는 블록 — TaskContent.openNextBlock 과 같은 규칙. 마지막·isEnd 블록이면 undefined
export function getNextBlockIndex(data: ProgramContentType[], index: number) {
  if (index === data.length - 1 || data[index].isEnd) return undefined;
  const nextBlockIndex = data.findIndex(
    (element, blockIndex) =>
      blockIndex > index &&
      element.blockId &&
      element.blockId === data[index].nextBlockId
  );
  return nextBlockIndex > -1 ? nextBlockIndex : index + 1;
}

// 칸 위치 키가 그대로 유효한지 — 블록·줄·칸 배치가 같은지 비교
export function isSameStructure(
  a: ProgramContentType[],
  b: ProgramContentType[]
) {
  const shape = (content: ProgramContentType[]) =>
    JSON.stringify(
      content.map((block) => block.lines.map((line) => line.map((c) => c.type)))
    );
  return shape(a) === shape(b);
}

// 자동 채우기 진행률 — 지금 보이는 필수 칸 기준 (고르지 않은 분기·감정의 칸, optional 칸 제외).
// 단계 = 필수 칸이 있는 블록, 그 블록의 필수 칸이 다 오면 끝난 단계
export function getAutoFillProgress(
  fields: UserSimField[],
  values: AutoFillValues
) {
  const chosen = (key: string) => {
    const value = values[key] ?? fields.find((f) => f.key === key)?.value;
    return Array.isArray(value) ? value : [value];
  };
  const required = fields.filter(
    (field) =>
      field.fill === "required" &&
      (!field.visible_if ||
        field.visible_if.options.some((option) =>
          chosen(field.visible_if!.key).includes(option)
        ))
  );
  const isWritten = (field: UserSimField) => values[field.key] !== undefined;
  const blocks = Array.from(new Set(required.map((field) => field.block)));
  return {
    written: required.filter(isWritten).length,
    total: required.length,
    doneSteps: blocks.filter((block) =>
      required.filter((f) => f.block === block).every(isWritten)
    ).length,
    totalSteps: blocks.length,
  };
}

export function isCellEmpty(cell: CellType) {
  return !`${cell.content.value ?? ""}`.trim();
}

// 자동 채우기 표시용 칸 키 — 여러 개 선택 칸의 선택지를 풀면 그 아래 줄이 당겨지므로, 칸 id 가 있으면 id 로 찾는다
export function getFilledCellKey(
  blockIndex: number,
  lineIndex: number,
  cellIndex: number,
  id?: string
) {
  return id
    ? `${blockIndex}-#${id}`
    : getCellKey(blockIndex, lineIndex, cellIndex);
}

// 자동 채우기로 새로 채워진 칸 — 표시용 칸 키 → 값 (선택지를 골라 붙은 칸 포함)
export function getFilledCells(
  before: ProgramContentType[],
  after: ProgramContentType[]
) {
  const filled: Record<string, string> = {};
  after.forEach((block, blockIndex) =>
    block.lines.forEach((line, lineIndex) =>
      line.forEach((cell, cellIndex) => {
        const prev = before[blockIndex]?.lines[lineIndex]?.[cellIndex];
        if (!isCellEmpty(cell) && (!prev || isCellEmpty(prev))) {
          const key = getFilledCellKey(
            blockIndex,
            lineIndex,
            cellIndex,
            cell.content.id
          );
          filled[key] = `${cell.content.value}`;
        }
      })
    )
  );
  return filled;
}

// 자동 채우기 값 그대로인지 — 여러 개 선택 칸("a&&b")은 고른 순서와 무관하게 비교
export function isAutoFilledValue(
  type: CellType["type"],
  filled?: string,
  value?: string
) {
  const normalize = (each?: string) =>
    type === "buttongroup_multipleChoice"
      ? each?.split("&&").sort().join("&&")
      : each;
  return filled !== undefined && normalize(filled) === normalize(value);
}

// 마치기 조건 검사용 — 소감 칸(코치에게 보내는 메시지)은 채워 두기만 하고 보내기는 사용자가 누르므로, 보낸 것(빈 칸)으로 본다
export function withFeedbackSent(content: ProgramContentType[]) {
  return content.map((block) => ({
    ...block,
    lines: block.lines.map((line) =>
      line.map((cell) =>
        cell.type === "feedback"
          ? { ...cell, content: { ...cell.content, value: "" } }
          : cell
      )
    ),
  }));
}

// 계약(docs/user-sim-agent.md)의 값 형식 검사 후 칸 content 로 변환. 형식이 틀리면 undefined
export function toCellContent(field: UserSimField, value: unknown) {
  if (field.input === "text") {
    if (typeof value !== "string" || !value.trim()) return undefined;
    // PlainTextarea 의 flush 와 같은 형식
    return { value, htmlString: `<p>${value}</p>` };
  }
  if (field.input === "percentage") {
    if (
      typeof value !== "number" ||
      !Number.isInteger(value) ||
      value < field.min ||
      value > field.max ||
      (!field.allow_zero && value === 0)
    ) {
      return undefined;
    }
    return { value: `${value}`, percentage: value };
  }
  if (field.input === "choice") {
    if (
      typeof value !== "number" ||
      !Number.isInteger(value) ||
      value < 0 ||
      value >= field.options.length
    ) {
      return undefined;
    }
    return { value: field.options[value], selectedIndex: value };
  }
  if (field.input === "multi_choice") {
    if (
      !Array.isArray(value) ||
      value.length === 0 ||
      new Set(value).size !== value.length ||
      value.some(
        (index) =>
          typeof index !== "number" ||
          !Number.isInteger(index) ||
          index < 0 ||
          index >= field.options.length
      ) ||
      (field.select_count !== null && value.length !== field.select_count)
    ) {
      return undefined;
    }
    // ButtonGroupMultipleChoiceCell 과 같은 형식
    return {
      value: value.map((index) => field.options[index]).join("&&"),
      selectedIndices: value,
    };
  }
  return undefined;
}

// 선택 칸을 고른 것과 같은 다음 블록 지정 (ButtonGroupCell·SelectCell 의 handleChange)
function setChoiceNextBlockId(
  block: ProgramContentType,
  cell: CellType,
  selectedIndex: number
) {
  const option = (cell.content as SingleSelectionType).options?.[selectedIndex];
  const prefix =
    cell.type === "select" ? "select_option" : "buttongroup_option";
  block.nextBlockId = option?.showFromId || `${prefix}_${cell.content.value}`;
}

// 빈 칸에 값을 채우고, 블록 진행과 같은 규칙으로 마지막 블록(또는 isEnd 블록)까지 연다.
// 값: fill 이 none 인 칸은 요청에 담긴 값(퀴즈 정답 등), 나머지는 Agent 값. 이미 채워진 칸은 덮어쓰지 않는다.
export function applyAutoFill(
  content: ProgramContentType[],
  fields: UserSimField[],
  values: AutoFillValues
): ProgramContentType[] {
  const data = _.cloneDeep(content);
  const fieldMap = new Map(fields.map((field) => [field.key, field]));

  const fill = (cell: CellType, field?: UserSimField) => {
    if (!field || !isCellEmpty(cell)) return undefined;
    const filled = toCellContent(
      field,
      field.fill === "none" ? field.value : values[field.key]
    );
    if (filled) cell.content = { ...cell.content, ...filled };
    return filled;
  };

  // 고른 선택지의 칸을 블록 끝에 붙이고 채운다 (ButtonGroupMultipleChoiceCell 의 handleChange)
  const appendOptionContent = (
    block: ProgramContentType,
    cell: CellType,
    fieldKey: string
  ) => {
    const { options, selectedIndices } = cell.content as MultipleSelectionType;
    selectedIndices?.forEach((optionIndex) => {
      const additionalContent = options?.[optionIndex]?.additionalContent;
      if (!additionalContent) return;
      const lines = _.cloneDeep(additionalContent);
      lines.forEach((line, lineIndex) =>
        line.forEach((optionCell, cellIndex) =>
          fill(
            optionCell,
            fieldMap.get(
              getOptionCellKey(fieldKey, optionIndex, lineIndex, cellIndex)
            )
          )
        )
      );
      block.lines.push(...lines);
    });
  };

  let blockIndex: number | undefined = 0;

  while (blockIndex !== undefined) {
    const currentIndex: number = blockIndex;
    const block = data[currentIndex];
    block.isShown = true;
    block.isHidden = false;

    block.lines.forEach((line, lineIndex) =>
      line.forEach((cell, cellIndex) => {
        const field = fieldMap.get(
          getCellKey(currentIndex, lineIndex, cellIndex)
        );
        const filled = fill(cell, field);
        if (field?.input === "choice" && filled) {
          setChoiceNextBlockId(block, cell, filled.selectedIndex as number);
        }
        if (field?.input === "multi_choice" && filled) {
          appendOptionContent(block, cell, field.key);
        }
        // 알림 허용 단계는 "허용 안 함" 으로 진행 (RequestPushNotificationButton 의 미허용 경로)
        const push = cell.content as PushNotificationType;
        if (cell.type === "pushNotification" && !push.clicked) {
          cell.content = { ...push, clicked: true };
          block.nextBlockId = push.showFromIdIfDenied;
        }
      })
    );

    blockIndex = getNextBlockIndex(data, currentIndex);
  }

  // 자동 계산 칸 (Cell.tsx 의 complementPercentageIdList 계산과 같음)
  data.forEach((block) =>
    block.lines.flat().forEach((cell) => {
      const idList = cell.content.complementPercentageIdList;
      if (cell.type === "percentage" && idList) {
        const percentage = Math.max(0, 100 - getPercentageSum(data, idList));
        cell.content = { ...cell.content, value: `${percentage}`, percentage };
      }
    })
  );

  return data;
}
